import React, { useState, useEffect, useCallback } from "react";
import { Copy, Check, QrCode, UploadCloud, Clock, CheckCircle2, XCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const DEPOSIT_ADDRESS = "0xc86318A284cdD526c9a5752B182b557c9eb26701";

const STATUS_BADGE = {
  pending: { cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", Icon: Clock, label: "Pending" },
  approved: { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", Icon: CheckCircle2, label: "Approved" },
  rejected: { cls: "bg-rose-500/15 text-rose-300 border-rose-500/30", Icon: XCircle, label: "Rejected" },
};

export default function FundRequest() {
  const [copied, setCopied] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [requests, setRequests] = useState([]);

  const loadRequests = useCallback(async () => {
    try {
      const r = await api.get("/me/fund-requests");
      setRequests(r.data.rows || []);
    } catch (err) {
      console.warn("Failed to load fund requests:", err?.message);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(DEPOSIT_ADDRESS);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = DEPOSIT_ADDRESS;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setMsg({ type: "err", text: "Please upload an image file (jpg, png, etc.)" });
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setMsg({ type: "", text: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    if (!file) { setMsg({ type: "err", text: "Please attach your payment screenshot." }); return; }
    if (!amount || Number(amount) < 5) { setMsg({ type: "err", text: "Minimum amount is $5." }); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("amount", String(Number(amount)));
      fd.append("note", note);
      fd.append("file", file);
      await api.post("/fund-request", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg({ type: "ok", text: "Payment proof submitted! Admin will review and credit your deposit wallet." });
      setAmount(""); setNote(""); setFile(null); setPreview(null);
      await loadRequests();
    } catch (err) {
      setMsg({ type: "err", text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="fund-request-page">
      <div>
        <div className="overline" data-testid="fund-request-overline">Fund Request</div>
        <h1 className="font-display text-4xl mt-1">Add Funds (USDT · BEP20)</h1>
        <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
          Send <strong className="text-neutral-200">USDT (BEP20 / BSC network)</strong> to the wallet
          address below. Scan the QR code or copy the address. After paying,
          <strong className="text-neutral-200"> upload your payment screenshot</strong> below — admin will
          verify and credit your deposit wallet.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Payment card */}
        <div className="lt-card p-6 md:p-8" data-testid="fund-request-card">
          <div className="flex items-center justify-center gap-2 mb-6">
            <QrCode size={18} className="text-[#D4AF37]" />
            <span className="font-display text-xl">USDT · BEP20</span>
          </div>

          <div className="bg-white rounded-xl p-4 w-full max-w-xs mx-auto">
            <img
              src="/usdt-bep20-qr.jpeg"
              alt="USDT BEP20 deposit QR code"
              className="w-full h-auto rounded-md"
              data-testid="fund-request-qr"
            />
          </div>

          <div className="mt-6">
            <div className="overline mb-2">Wallet Address</div>
            <div
              className="px-4 py-3 rounded-lg border border-white/10 bg-white/[0.03] text-sm tabular break-all text-neutral-100"
              data-testid="fund-request-address"
            >
              {DEPOSIT_ADDRESS}
            </div>
            <button
              onClick={copyAddress}
              className="lt-btn-primary w-full mt-3 inline-flex items-center justify-center gap-2"
              data-testid="fund-request-copy-btn"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Copied!" : "Copy Address"}
            </button>
          </div>

          <div className="text-[11px] text-amber-300/80 mt-4 text-center">
            Only send USDT on the BEP20 (BSC) network to this address. Sending any other
            token or network may result in permanent loss.
          </div>
        </div>

        {/* Upload proof card */}
        <div className="lt-card p-6 md:p-8" data-testid="fund-proof-card">
          <div className="flex items-center gap-2 mb-5">
            <UploadCloud size={18} className="text-[#D4AF37]" />
            <span className="font-display text-xl">Upload Payment Proof</span>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline block mb-2">Amount Paid (USDT)</label>
              <input
                type="number" min="5" step="1" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 100"
                className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-neutral-100 outline-none focus:border-[#D4AF37]/50"
                data-testid="fund-proof-amount"
              />
            </div>

            <div>
              <label className="overline block mb-2">Transaction Note / Hash (optional)</label>
              <input
                type="text" value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Tx hash or reference"
                className="w-full px-4 py-3 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-neutral-100 outline-none focus:border-[#D4AF37]/50"
                data-testid="fund-proof-note"
              />
            </div>

            <div>
              <label className="overline block mb-2">Payment Screenshot</label>
              <label
                className="flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border border-dashed border-white/15 bg-white/[0.02] cursor-pointer hover:border-[#D4AF37]/40 transition-colors"
                data-testid="fund-proof-dropzone"
              >
                {preview ? (
                  <img src={preview} alt="proof preview" className="max-h-44 rounded-md" data-testid="fund-proof-preview" />
                ) : (
                  <>
                    <UploadCloud size={28} className="text-neutral-500" />
                    <span className="text-sm text-neutral-400">Click to select an image</span>
                  </>
                )}
                <input
                  type="file" accept="image/*" onChange={onFileChange} className="hidden"
                  data-testid="fund-proof-file"
                />
              </label>
            </div>

            {msg.text && (
              <div
                className={`text-sm px-3 py-2 rounded-lg ${msg.type === "ok" ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`}
                data-testid="fund-proof-msg"
              >
                {msg.text}
              </div>
            )}

            <button
              type="submit" disabled={submitting}
              className="lt-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="fund-proof-submit"
            >
              {submitting ? "Submitting…" : "Submit Payment Proof"}
            </button>
          </form>
        </div>
      </div>

      {/* My requests */}
      <div className="lt-card p-6" data-testid="fund-requests-history">
        <div className="overline mb-4">My Fund Requests</div>
        {requests.length === 0 ? (
          <div className="text-sm text-neutral-500">No fund requests yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lt-table min-w-[560px]">
              <thead>
                <tr>
                  <th>Date</th><th>Amount</th><th>Note</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const b = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                  return (
                    <tr key={r.id} data-testid={`fund-request-row-${r.id}`}>
                      <td className="text-neutral-300">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="tabular gold-text">${Number(r.amount).toFixed(2)}</td>
                      <td className="text-neutral-400 max-w-[200px] truncate">{r.note || "—"}</td>
                      <td>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${b.cls}`}>
                          <b.Icon size={12} /> {b.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
